import { useEffect, useState } from 'react';
import { EXIF_SCAN_BYTES, readExif, type PhotoMeta } from '../lib/exif';
import { loadLocalFile } from '../lib/localMedia';
import { endpoints, type Place } from '../services/api';
import type { MediaItem } from '../store/settings';

const NO_META: PhotoMeta = { takenAt: null, latitude: null, longitude: null };

// Device files are read once per session; the EXIF block is in the first few KB.
const localMeta = new Map<string, Promise<PhotoMeta>>();

function readLocalMeta(id: string): Promise<PhotoMeta> {
  let p = localMeta.get(id);
  if (!p) {
    p = loadLocalFile(id)
      .then((blob) => (blob ? blob.slice(0, EXIF_SCAN_BYTES).arrayBuffer().then(readExif) : NO_META))
      .catch(() => NO_META);
    localMeta.set(id, p);
  }
  return p;
}

/** When and where a slide was taken: from OneDrive's metadata, or the EXIF of a device file. */
export function usePhotoMeta(item: MediaItem | null): PhotoMeta | null {
  const [local, setLocal] = useState<{ id: string; meta: PhotoMeta } | null>(null);
  const localImage = item?.local && item.kind === 'image' ? item.id : null;

  useEffect(() => {
    if (!localImage) return;
    let live = true;
    void readLocalMeta(localImage).then((meta) => live && setLocal({ id: localImage, meta }));
    return () => {
      live = false;
    };
  }, [localImage]);

  if (!item) return null;
  if (item.local) return local?.id === item.id ? local.meta : null;
  return { takenAt: item.takenAt ?? null, latitude: item.latitude ?? null, longitude: item.longitude ?? null };
}

// Same rounding as the server (~100 m), so nearby photos share one lookup.
const places = new Map<string, Promise<Place | null>>();
const lang = (() => {
  try {
    return navigator.language || 'en';
  } catch {
    return 'en';
  }
})();

/** Town / district / country names for a position (null while loading or unknown). */
export function usePlace(lat: number | null, lon: number | null): Place | null {
  const [state, setState] = useState<{ key: string; place: Place | null } | null>(null);
  const key = lat !== null && lon !== null ? `${lat.toFixed(3)},${lon.toFixed(3)}` : null;

  useEffect(() => {
    if (!key || lat === null || lon === null) return;
    let p = places.get(key);
    if (!p) {
      p = endpoints.reversePlace(Number(lat.toFixed(3)), Number(lon.toFixed(3)), lang).then((r) => r.place);
      // Failures (busy, offline) are retried the next time the photo comes round.
      p.catch(() => places.delete(key));
      places.set(key, p);
    }
    let live = true;
    p.then((place) => live && setState({ key, place })).catch(() => undefined);
    return () => {
      live = false;
    };
  }, [key, lat, lon]);

  return state && state.key === key ? state.place : null;
}
